import { useIntercom } from '@app/services/intercom/IntercomContext';
import { ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import NewRatingsThresholdSlider from '@shared/components/new-ratings-threshold-slider';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Button } from '@shared/components/ui/button';
import { cn, formatNumber } from '@shared/lib/utils';

interface ProductDetailCarbonRatingCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  productResult: ProductResult;
  showLearnMore?: boolean;
  pdf?: boolean;
}

export function ProductDetailCarbonRatingCard({
  productResult,
  className,
  showLearnMore = true,
  pdf = false,
  ...props
}: ProductDetailCarbonRatingCardProps) {
  const { showIntercomArticle } = useIntercom();

  return (
    <div
      className={cn(
        'flex w-full flex-col space-y-4 rounded-md  bg-gray-00 px-3 py-3',
        { 'p-0 space-y-2': pdf },
        className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <Text
          variant="cardTitle"
          className={cn('text-primary-900', {
            'font-heading text-md font-bold text-primary-700': pdf,
          })}
          data-testid="carbon-rating-card-title"
        >
          Carbon rating
        </Text>
        {showLearnMore && (
          <Button asChild>
            <span
              className="flex cursor-pointer items-center text-sm text-gray-500"
              onClick={() =>
                showIntercomArticle('understandingCarbonDataAndLabels')
              }
            >
              <Icons.info className="mr-1" size={16} />
              Learn more
            </span>
          </Button>
        )}
      </div>
      <Text variant="subtle" className={cn({ 'text-xs': pdf })}>
        The emissions per kilogram of food produced, communicated on our A-E
        rating scale.
      </Text>
      <div className="flex w-full items-end gap-4  pt-2">
        <RatingCloud
          rating={productResult.emissionsCategory!}
          format="png"
          className="w-20"
        />
        <div className="w-full flex-1">
          <span className="flex items-baseline gap-1">
            <Text className="text-4xl font-medium text-primary-800">
              {formatNumber(productResult.totalEmissionsPerKg!)}
            </Text>
            <Unit variant="kgCO2e/kg" className="text-md" />
          </span>
        </div>
      </div>
      <NewRatingsThresholdSlider
        className="my-auto w-full  flex-1 justify-center"
        rating={productResult.emissionsCategory!}
        value={productResult.totalEmissionsPerKg!}
        methodologyVersion={2}
        showValue={false}
        showClouds={false}
        showBandTooltips
      />
    </div>
  );
}
